import { useState } from "react";
import { Modal } from "./Modal";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useNavigate } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

export const AuthModal = ({ open, onClose }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        onClose();
        navigate("/dashboard");
      } else {
        await api.post("/auth/forgot-password", { email: form.email });
        setResetSent(true);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} testId="auth-modal">
      <div className="p-8 md:p-10">
        <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Client Portal</p>
        <h2 className="mt-2 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight">
          {mode === "login" ? "Welcome Back" : "Reset Your Password"}
        </h2>
        {mode === "forgot" && resetSent ? (
          <div className="mt-8 text-center py-6" data-testid="reset-sent-message">
            <MailCheck size={32} className="text-[#D4AF37] mx-auto" />
            <p className="mt-4 text-sm text-[#64748B] leading-relaxed">
              If an account exists for <span className="font-medium text-[#0B1B2B]">{form.email}</span>, a secure reset
              link has been dispatched. The link is valid for one hour.
            </p>
            <button
              onClick={() => { setMode("login"); setResetSent(false); }}
              className="mt-6 text-sm font-semibold text-[#0B1B2B] gold-underline"
              data-testid="back-to-login-button"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <input className={inputCls} type="email" placeholder="Email" required value={form.email} onChange={set("email")} data-testid="auth-email-input" />
            {mode === "login" && (
              <input className={inputCls} type="password" placeholder="Password" required value={form.password} onChange={set("password")} data-testid="auth-password-input" />
            )}
            {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#0B1B2B] text-white font-medium py-3.5 rounded-xl hover:bg-[#152a40] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="auth-submit-button"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {mode === "login" ? "Sign In" : "Send Reset Link"}
            </button>
            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setError(""); }}
                className="text-xs font-semibold text-[#64748B] hover:text-[#0B1B2B] gold-underline"
                data-testid="auth-forgot-toggle"
              >
                {mode === "login" ? "Forgot password?" : "Back to Sign In"}
              </button>
            </div>
            <p className="text-xs text-[#64748B] leading-relaxed border-t border-[#E2E8F0] pt-4">
              New to Acuity Rise? Portal access is provisioned automatically once your intake submission is approved by
              our executive review team — credentials will arrive by email.
            </p>
          </form>
        )}
      </div>
    </Modal>
  );
};
